import {
  ArchiveBoxArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowLeftCircleIcon,
  ArrowPathIcon,
  ArrowRightCircleIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ClockIcon,
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  EyeIcon,
  // PencilSquareIcon,
  MagnifyingGlassIcon,
  PrinterIcon,
  SwatchIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { DOCUMENTS_PANEL_WIDTH, notifyDocumentsPanel } from 'components/AssistantModal/assistantPanelLayout'
import { SourceBox, TargetBox } from 'components/DragDrop'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { baseUrl, DocumentsRefreshInterval } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { docCategoryFor, docStatus } from 'pages/LoanSubmission/constants'
import { LoanSendEmailDialog } from 'pages/LoanSubmission/Modals/sendEmailDialog'
import { NewDocumentsTable } from 'pages/LoanSubmission/NewDocumentsTable'
import { LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { MailDocumentModal } from 'pages/Servicing/PrintMail/MailDocumentModal'
import { usePrintMailOffer } from 'pages/Servicing/PrintMail/usePrintMailOffer'
import { useEffect, useMemo, useRef, useState } from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import {
  analyzeDocument,
  downloadS3Document,
  extractDocumentBySections,
  getLoanSubmissionUpdatedAt,
  getTemporaryUserInfo,
  updateBorrower,
  uploadFiles,
} from 'services'
import Api from 'services/api'
import { Checkbox, DocumentFile, HorzScrollContainer, Input2, Select, Toggle, Tooltip } from 'stories/components'
import { capitalizeFirstLetter, confirm, formatTime, getFileExt, hasFileExt } from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'
import { setLoanNumber } from 'utils/setLoanNumber'

import { ExtractDocumentFilesModal, ExtractDocumentHistoryModal, ExtractDocumentSectionsModal } from './analyzeDocument'
import { DocumentSection, FileExtsForAnalyze } from './analyzeDocument/types'
import { copyDroppedDocuments, isForeignDocumentDrop } from './droppedDocuments'
import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

export const DocumentsSlide = ({
  submitToLoanSetup,
  brokerProfile,
  loading,
  setLoading,
  documents,
  onAdd,
  onUpdate,
  onOpen,
  onRemove: _onRemove,
  onSelectedRemove: _onSelectedRemove,
  onOpenDocHistory,
  onOpenAllDocHistory,
  onVisibilityChange,
  refetchDocuments,
}: any) => {
  const token = useSelector((state: any) => state.auth.token)
  const [isShowDocuments, setShowDocuments] = useState(false)
  const floater = useFloaterTab('documents', { side: 'right', percent: 25, mobileTop: 192 })
  const [searchQuery, setSearchQuery] = useState('')
  // const [name, setName] = useState('')
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [sortKey, setSortKey] = useState('')
  const [sortDir, setSortDir] = useState(true)
  const [uploadingFiles, setUploadingFiles] = useState<DocumentFile[]>([])
  const [isEmailDialog, showEmailDialog] = useState(false)
  const [selectedDocuments, setSelectedDocuments] = useState<LoanSubmissionDocument[]>([])
  const [downloadStatus, setDownloadStatus] = useState('All')

  const [modalAction, setModalOpen] = useState('')
  const [activeDocument, setActiveDocument] = useState<LoanSubmissionDocument | null>(null)
  const [activeDocumentData, setActiveDocumentData] = useState<any>(null)
  const [previousStatuses, setPreviousStatuses] = useState<Record<string, string> | null>(null)
  const lastUpdatedAtRef = useRef<number>(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { hasPermission } = usePermissions()
  const canDeleteDocumentPermanently = hasPermission('Loan.Submission.Condition.Document.Delete')
  const canDocumentAnalyzeExtract = hasPermission('Loan.Document.ExtractTitle')
  const canViewInternalDocuments = hasPermission('Loan.Submission.Document.Internal')
  const canSpendPostage = !!hasPermission('PrintMail.Send')
  // Only asked for once the panel is actually open, and only for somebody who could post anything.
  // `canOffer` is false unless the company has print and mail on AND has switched on at least one
  // communication type, so a tenant that has not decided to post anything is never offered it.
  const { canOffer: canMailDocuments } = usePrintMailOffer(isShowDocuments && canSpendPostage)
  const [mailingDocument, setMailingDocument] = useState<LoanSubmissionDocument | null>(null)

  useEffect(() => {
    if (!isShowDocuments) return

    let cancelled = false

    const pollUpdatedAt = () => {
      getLoanSubmissionUpdatedAt()
        .then((res) => {
          if (cancelled) return
          if (res && res !== lastUpdatedAtRef.current) {
            if (lastUpdatedAtRef.current) refetchDocuments()
            lastUpdatedAtRef.current = res
          }
        })
        .catch((error) => {
          if (cancelled) return
          console.error('Error fetching documents updated at:', error)
        })
    }

    pollUpdatedAt()
    const intervalId = setInterval(pollUpdatedAt, DocumentsRefreshInterval)

    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [isShowDocuments])

  const sortedDocuments = useMemo<LoanSubmissionDocument[]>(() => {
    const query = searchQuery.trim().toLowerCase()
    let docs = documents
      .filter((doc: any) => {
        if (downloadStatus === 'Selected') {
          if (!selected[doc.key]) return false
        } else if (downloadStatus === 'Approved + AI Approved') {
          if (doc.status !== 'Approved' && doc.status !== 'AI Approved') return false
        } else if (downloadStatus !== 'All') {
          if (doc.status !== downloadStatus) return false
        }

        if (!query) return true
        return (
          String(doc.conditionNo || '')
            .toLowerCase()
            .includes(query) ||
          (doc.name || '').toLowerCase().includes(query) ||
          (doc.status || '').toLowerCase().includes(query) ||
          (doc.category || '').toLowerCase().includes(query)
        )
      })
      .sort((a: any, b: any) => {
        const aV = a[sortKey]
        const bV = b[sortKey]
        if (aV > bV) return 1
        if (aV == bV) return 0
        return -1
      })
    if (sortDir) docs = docs.reverse()
    return docs
  }, [documents, sortDir, sortKey, searchQuery, downloadStatus, selected])

  const isSelectedBtnDisabled = useMemo(() => Object.keys(selected).length == 0, [selected])

  const isDownloadDisabled = useMemo(
    () => documents.length == 0 || (downloadStatus === 'Selected' && Object.keys(selected).length == 0),
    [documents, downloadStatus, selected],
  )

  const onUpdateDocument = (key: string, value: string | boolean, documentKey: string) => {
    const index = documents.findIndex((doc: any) => doc.key == documentKey)
    if (index == -1) return
    const newDocs = cloneDeep(documents)

    if (key == 'name') {
      newDocs[index].isEditing = false
      const orgName = newDocs[index].name || newDocs[index].key?.split('/').pop() || ''
      value = value + (hasFileExt(orgName) ? orgName.substr(-4) : '')
    }

    newDocs[index][key] = value

    if (previousStatuses) setPreviousStatuses(null)
    onUpdate(newDocs, newDocs[index])
  }

  const onShowDocuments = () => {
    const nextState = !isShowDocuments
    setShowDocuments(nextState)
    notifyDocumentsPanel({ open: nextState, side: floater.side, width: DOCUMENTS_PANEL_WIDTH })
    onVisibilityChange?.(nextState)
  }

  const onSelected = (key: string, value: boolean) => {
    const newData = cloneDeep(selected)
    if (!value) delete newData[key]
    else newData[key] = value
    setSelected(newData)
  }

  const onSelectAll = () => {
    const count = documents.length
    const selCount = Object.keys(selected).length
    let newData = cloneDeep(selected)
    if (count != selCount) documents.forEach((doc: any) => (newData[doc.key] = true))
    else newData = {}
    setSelected(newData)
  }

  const onSort = (key: string) => {
    setSortKey(key)
    if (sortKey == key) setSortDir(!sortDir)
  }

  const onDownload = (format: 'zip' | 'pdf', type: string, status?: string) => {
    const loanNumber = Api.getLoanNumber()
    let url = `${baseUrl}/loan/downloadSubmissionDocuments/${loanNumber}/${type}?token=${token}&format=${format}`
    if (type == 'selected') {
      const selectedIndexes = Object.keys(selected).map((key) =>
        documents.findIndex((doc: Record<string, any>) => doc.key == key),
      )
      url += `&keys=${selectedIndexes.join(',')}`
    }
    if (type == 'status' && status) {
      url += `&status=${encodeURIComponent(status)}`
    }

    var windowReference: any = window.open()
    windowReference.document.title = 'Downloading Documents...'
    windowReference.location = url
  }

  const onDownloadStatus = (format: 'zip' | 'pdf') => {
    if (downloadStatus === 'All') onDownload(format, 'all')
    else if (downloadStatus === 'Selected') onDownload(format, 'selected')
    else if (downloadStatus === 'Approved + AI Approved') onDownload(format, 'status', 'Approved,AI Approved')
    else onDownload(format, 'status', downloadStatus)
  }

  const onSelectedRemove = async () => {
    const indexes = Object.keys(selected)
      .map((key) => documents.findIndex((doc: Record<string, any>) => doc.key == key))
      .filter((v) => v != -1)
      .sort((a, b) => a - b)
    if (indexes.length == 0) {
      setSelected({})
      return
    }

    const content = (
      <div className="text-gray-900 mb-4 text-md">
        Are you sure want to delete this documents permanently?
        <div className="text-gray-800 flex flex-col text-left text-sm mt-4">
          {indexes.map((index) => (
            <span key={documents[index].key}>- {documents[index] && documents[index].name}</span>
          ))}
        </div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    await _onSelectedRemove(Object.keys(selected))
    setSelected({})
  }

  const onRemove = (document: LoanSubmissionDocument) => {
    setSelected({})
    _onRemove(document)
  }

  const onAnalyzeDocument = async (document: LoanSubmissionDocument) => {
    const result = await confirm('Are you sure want to analyze this document?')
    if (!result) return

    setLoading(true)
    const res = await analyzeDocument(document)
    const hasSections = res.success && res.sections?.length > 0
    const hasFiles = res.success && res.files?.length > 0
    if (hasSections || hasFiles) {
      setModalOpen('analyzeDocument')
      setActiveDocument(document)
      setActiveDocumentData(res)
      if (hasFiles && typeof refetchDocuments === 'function') await refetchDocuments()
    }
    setLoading(false)
  }

  const onExtractAndSaveSections = async (sections: DocumentSection[]) => {
    if (!activeDocument?.key) return

    const result = await confirm('Are you sure want to extract and save sections to document storage?')
    if (!result) return

    setLoading(true)
    await extractDocumentBySections(activeDocument.key, sections)
    setLoading(false)

    setModalOpen('')
    setActiveDocumentData(null)
  }

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (files?.length) onUploadFiles(Array.from(files))
    e.target.value = ''
  }

  const onUploadFiles = async (files: File[]) => {
    setLoading(true)
    const loanNumber = setLoanNumber()
    const uploadData = {
      path: `loan/${loanNumber}/document`,
    }
    let keys: string[] = []
    try {
      keys = await uploadFiles(uploadData, files)
    } catch {
      // All the files go up in one request, so a failure means none of them landed. Without this the
      // loading overlay never lifts and the panel can only be recovered by reloading the page.
      return
    } finally {
      setLoading(false)
    }
    const now = Date.now()

    const newFiles = cloneDeep(uploadingFiles)
    newFiles.push(
      ...files.map((file, index) => ({
        id: now + index,
        name: file.name,
        fileKey: keys[index],
        status: 'Not Reviewed',
        category: '',
        createdAt: now,
      })),
    )
    setUploadingFiles(newFiles)
  }

  /**
   * A drop on the storage is either OS files, which upload, or files the loan already holds
   * elsewhere (an exception's attachments, dragged over from the Loan Structure page), which are
   * copied in. Both land in the same categorize-and-save table as a fresh upload.
   */
  const onDropFiles = async (item: Record<string, any>) => {
    if (item?.files) return onUploadFiles(item.files as File[])
    const heldKeys = documents.map((doc: LoanSubmissionDocument) => doc.key)
    setLoading(true)
    let copied: DocumentFile[] = []
    try {
      copied = await copyDroppedDocuments(item, setLoanNumber(), heldKeys)
    } catch {
      return
    } finally {
      setLoading(false)
    }
    if (copied.length) setUploadingFiles([...cloneDeep(uploadingFiles), ...copied])
  }

  const OnSendEmail = () => {
    const selectedDocuments = sortedDocuments.filter((doc: any) => selected[doc.key])
    setSelectedDocuments(selectedDocuments)
    showEmailDialog(true)
  }

  const onSelectProfile = async () => {
    const loanNumber = setLoanNumber()
    const selectedDocuments = sortedDocuments.filter((doc: any) => selected[doc.key])
    const borrower = await getTemporaryUserInfo(loanNumber)
    const options = [{ name: 'General', value: 'general' }]
    borrower.entities.map((v: string) => {
      options.push({ name: capitalizeFirstLetter(v), value: v })
    })
    const result: any = await confirmOptions('Please select where document(s) should be assigned', options)
    if (result === false) return
    const data: Record<string, any> = {}
    data['email'] = borrower.email
    data['documents'] = [...borrower.documents]
    selectedDocuments.map((newDoc: any) => {
      let isExisting = false
      for (let i = 0; i < borrower.documents.length; i++) {
        if (borrower.documents[i].fileKey === newDoc.key) {
          isExisting = true
          break
        }
      }
      if (!isExisting)
        data['documents'].push({
          name: newDoc.name,
          fileKey: newDoc.key,
          status: newDoc.status,
          category: newDoc.category,
          section: result,
          createdAt: Date.now(),
        })
    })
    setLoading(true)
    await updateBorrower(borrower.id, data)
    setLoading(false)
  }

  const onAnalyzeDocumentLogs = async () => {
    setModalOpen('analyzeDocumentLogs')
  }

  const onResetAllStatuses = async () => {
    const result = await confirm('Are you sure you want to proceed to reset the status of all documents?')
    if (!result) return

    const saved: Record<string, string> = {}
    const newDocs = cloneDeep(documents)
    newDocs.forEach((doc: any) => {
      saved[doc.key] = doc.status || ''
      doc.status = 'Not Reviewed'
    })

    setPreviousStatuses(saved)
    await onUpdate(newDocs, newDocs)
  }

  const onUndoResetStatuses = async () => {
    if (!previousStatuses) return

    const newDocs = cloneDeep(documents)
    newDocs.forEach((doc: any) => {
      if (previousStatuses[doc.key] !== undefined) {
        doc.status = previousStatuses[doc.key]
      }
    })

    setPreviousStatuses(null)
    await onUpdate(newDocs, newDocs)
  }

  const onEmailDialogClose = async (result: boolean) => {
    if (!result) {
      showEmailDialog(false)
      return
    }
    showEmailDialog(false)
  }

  const renderHeader = (key: string, title: any) => {
    return (
      <div className="flex flex-wrap font-normal items-center">
        <p>{title}</p>

        <span className="ml-2">
          {sortKey == key &&
            (sortDir ? <BarsArrowUpIcon className="w-3 h-3" /> : <BarsArrowDownIcon className="w-3 h-3" />)}
        </span>
      </div>
    )
  }

  const renderRow = (document: LoanSubmissionDocument, index: number) => {
    const clsName = document.status?.replace(' ', '')
    const isInternalRestricted = !!document.intext && !canViewInternalDocuments
    const displayName = document.name || document.key?.split('/').pop() || ''
    const getDragItem = () => {
      const selectedDocs = sortedDocuments.filter((doc) => selected[doc.key])
      if (selectedDocs.length > 1 && selected[document.key]) {
        return {
          ...document,
          fileKey: document.key,
          documents: selectedDocs.map((doc) => ({ ...doc, fileKey: doc.key })),
        }
      }
      return { ...document, fileKey: document.key }
    }
    return [
      <SourceBox item={getDragItem} type="document" key={document.key}>
        <tr className={`bg-gray-50 ${clsName}`} key={`first-${document.key}`}>
          <td className="px-3">
            <Checkbox
              id={`condition-no-${document.key}`}
              title={`${document.conditionNo || 'Other'}`}
              checked={!!selected[document.key]}
              onChange={(value) => onSelected(document.key, value)}
            />
          </td>
          <td colSpan={3}>
            {(brokerProfile && submitToLoanSetup === 1) || isInternalRestricted ? (
              <span className="italic text-[13.5px]">{displayName}</span>
            ) : (
              <PlainInput
                value={hasFileExt(displayName) ? displayName.substr(0, displayName.length - 4) : displayName}
                content={displayName}
                isEditing={document.isEditing}
                onChange={(newName: string) => onUpdateDocument('name', newName, document.key)}
                additionalElements={document.fileOrigin ? <CheckBadgeIcon className="w-4 h-4 text-green-500" /> : null}
                className='break-all'
              />
            )}
          </td>
          <td rowSpan={2}>
            <div className="grid grid-flow-col grid-rows-2">
              <Tooltip message="View Document" placement="right">
                <button
                  className={`p-1 rounded flex justify-center ${
                    isInternalRestricted
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'cursor-pointer hover-shadow1 text-shade-blue'
                  }`}
                  disabled={isInternalRestricted}
                  onClick={() => !isInternalRestricted && onOpen(document)}
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              </Tooltip>

              <Tooltip message="Download Document" placement="right">
                <button
                  className={`p-1 rounded flex justify-center ${
                    isInternalRestricted ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer hover-shadow1'
                  }`}
                  disabled={isInternalRestricted}
                  onClick={() => !isInternalRestricted && downloadS3Document(document.key, document.name)}
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              </Tooltip>

              {canMailDocuments && !isInternalRestricted && (
                <Tooltip message="Print and post this document" placement="right">
                  <button
                    className="p-1 cursor-pointer hover-shadow1 rounded flex justify-center text-shade-blue"
                    onClick={() => setMailingDocument(document)}
                  >
                    <PrinterIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              )}

              {/* <button
                className="p-1 cursor-pointer hover-shadow1 rounded flex justify-center"
                onClick={() => onUpdateDocument('isEditing', true, document.key)}
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button> */}

              {!brokerProfile && (
                <Tooltip message="View History" placement="right">
                  <button
                    className="p-1 cursor-pointer hover-shadow1 rounded flex justify-center text-gray-600"
                    onClick={() => onOpenDocHistory(document)}
                  >
                    <ClockIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              )}

              {canDeleteDocumentPermanently && (
                <Tooltip message="Delete Document" placement="right">
                  <button
                    className="p-1 cursor-pointer hover-shadow1 rounded flex justify-center text-red-800"
                    onClick={() => onRemove(document)}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              )}

              {canDocumentAnalyzeExtract && document.key && FileExtsForAnalyze.includes(getFileExt(document.key)) && (
                <Tooltip message="Analyze & Extract Document" placement="right">
                  <button
                    className="p-1 cursor-pointer hover-shadow1 rounded flex justify-center text-gray-800"
                    onClick={() => onAnalyzeDocument(document)}
                  >
                    <SwatchIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              )}
            </div>
          </td>
        </tr>
        <tr className={`border-b ${clsName}`} key={`second-${document.key}`}>
          <td className="px-2">
            <div className="flex items-center gap-2 justify-center">
              {!brokerProfile && (
                <div className="scale-75">
                  <Toggle
                    key={`intext-${document.key}-${document.intext}`}
                    id={`intext-${index}`}
                    className="flex-col"
                    value={document.intext}
                    tooltip="Internal"
                    disabled={!canViewInternalDocuments}
                    onChange={(value) => onUpdateDocument('intext', value, document.key)}
                  />
                </div>
              )}
              <div className="scale-75">
                <Toggle
                  key={`borrower-${document.key}-${document.borrower}`}
                  id={`borrower-${index}`}
                  className="flex-col"
                  value={document.borrower}
                  tooltip="Borrower"
                  disabled={isInternalRestricted}
                  onChange={(value) => onUpdateDocument('borrower', value, document.key)}
                />
              </div>
              {/* Publishes the document to the loan's participants in the participant portal - the
                  participant twin of the Borrower toggle, read the same way. */}
              <div className="scale-75">
                <Toggle
                  key={`participant-${document.key}-${document.participant}`}
                  id={`participant-${index}`}
                  className="flex-col"
                  value={document.participant}
                  tooltip="Participant"
                  disabled={isInternalRestricted}
                  onChange={(value) => onUpdateDocument('participant', value, document.key)}
                />
              </div>
            </div>
          </td>
          <td className="px-1">
            {brokerProfile ? (
              <span className="italic text-[13.5px]">{document.status}</span>
            ) : (
              <Select
                id={`side-document-status-${document.key}`}
                size={3}
                className="-mb-4 bg-white"
                options={docStatus}
                value={document.status}
                disabled={isInternalRestricted}
                onChange={(value) => onUpdateDocument('status', value, document.key)}
              />
            )}
          </td>

          <td className="px-1">
            {brokerProfile && submitToLoanSetup === 1 ? (
              <span className="italic text-[13.5px]">{document.category}</span>
            ) : (
              <Select
                id={`side-document-category-${document.key}`}
                size={3}
                className="-mb-4 bg-white"
                options={docCategoryFor(document.category).options}
                value={docCategoryFor(document.category).value}
                disabled={isInternalRestricted}
                onChange={(value) => onUpdateDocument('category', value, document.key)}
                sort
              />
            )}
          </td>

          <td className="">{formatTime(document.createdAt)}</td>
        </tr>
      </SourceBox>,
    ]
  }

  return (
    <>
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ring-shade-blue bg-shade-blue text-white border hover:bg-white border-shade-blue hover:text-shade-blue rounded flex items-center gap-2 w-auto sm:w-[155px]`}
          {...floater.tabProps(onShowDocuments)}
        >
          <BookOpenIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Documents</span>
        </button>
      </FloaterTabWrapper>
      {isShowDocuments && (
        <div
          className={`shadow1 fixed bg-white border z-50 top-[5vh] bottom-0 w-[calc(40rem+4px)] max-w-full h-[90vh] ease-in-out ${floaterPanelEdgeClass(
            floater.side,
          )}`}
        >
          <LayoutLoading show={loading} />
          <HorzScrollContainer width={6} size={documents.length}>
            <>
              {!uploadingFiles.length && (
                <div className="absolute top-12 right-[calc(50%-135px)] w-full h-fit">
                  <div className="flex flex-wrap items-center absolute top-0 right-0 border-dashed border-2 border-gray-300 px-4 py-8 text-gray-300 w-[270px] text-center">
                    <CloudArrowUpIcon className="w-10 h-10 mx-auto" />
                    <span>Drag and drop files.</span>
                  </div>
                </div>
              )}

              <div className="relative w-[40rem] max-w-full z-20 rounded-t font-bold text-sm text-shade-blue">
                <div className="flex justify-between place-items-center items-center border-b p-3">
                  <p className="text-lg text-gray-800">Documents</p>

                  <div className="flex items-center gap-1">
                    <Select
                      id="download-status-select"
                      size={3}
                      className="-mb-4 bg-white min-w-[110px]"
                      options={['All', 'Selected', ...docStatus, 'Approved + AI Approved']}
                      value={downloadStatus}
                      onChange={(value) => setDownloadStatus(value)}
                    />
                    <button
                      className={`${isDownloadDisabled ? 'text-blue-400 cursor-not-allowed' : 'rounded hover-shadow1'}`}
                      onClick={() => onDownloadStatus('zip')}
                      disabled={isDownloadDisabled}
                    >
                      <div className="flex p-1">
                        <Tooltip message="Download as Zip" position="bottom">
                          <ArchiveBoxArrowDownIcon className="w-5 h-5" />
                        </Tooltip>
                      </div>
                    </button>
                    <button
                      className={`${isDownloadDisabled ? 'text-blue-400 cursor-not-allowed' : 'rounded hover-shadow1'}`}
                      onClick={() => onDownloadStatus('pdf')}
                      disabled={isDownloadDisabled}
                    >
                      <div className="flex p-1">
                        <Tooltip message="Download as combined PDF" position="bottom">
                          <DocumentArrowDownIcon className="w-5 h-5" />
                        </Tooltip>
                      </div>
                    </button>
                  </div>

                  <div className="flex items-center">
                    <Checkbox
                      title={`${Object.keys(selected).length}/${documents.length}`}
                      id="documentSelected"
                      checked={Object.keys(selected).length == documents.length}
                      onClick={onSelectAll}
                      disabled={documents.length == 0}
                    />
                    <div className="flex items-center">
                      {canDeleteDocumentPermanently && (
                        <button
                          className={`${
                            isSelectedBtnDisabled ? 'text-red-400' : 'hover:underline hover-shadow1 rounded'
                          } text-red-600`}
                          onClick={() => onSelectedRemove()}
                          disabled={isSelectedBtnDisabled}
                        >
                          <div className="flex p-1">
                            <TrashIcon className="w-4 h-4" />
                          </div>
                        </button>
                      )}
                      <button
                        onClick={OnSendEmail}
                        disabled={isSelectedBtnDisabled}
                        className={`${isSelectedBtnDisabled ? 'text-blue-400' : 'rounded hover-shadow1'}`}
                      >
                        <div className="flex p-1">
                          <Tooltip message="Send Email" position="bottom">
                            <EnvelopeIcon className="w-5 h-5" />
                          </Tooltip>
                        </div>
                      </button>
                      <button
                        onClick={onSelectProfile}
                        disabled={isSelectedBtnDisabled}
                        className={`${isSelectedBtnDisabled ? 'text-blue-400' : 'rounded hover-shadow1'}`}
                      >
                        <div className="flex p-1">
                          <Tooltip message="Send documents to Borrower Profile" placement="right" position="bottom">
                            <UserIcon className="w-5 h-5" />
                          </Tooltip>
                        </div>
                      </button>
                      <button onClick={onAnalyzeDocumentLogs} className={`rounded hover-shadow1`}>
                        <div className="flex p-1">
                          <Tooltip message="Analyze & Extract Document Logs" placement="right" position="bottom">
                            <SwatchIcon className="w-5 h-5" />
                          </Tooltip>
                        </div>
                      </button>
                      {!brokerProfile && (
                        <button onClick={() => onOpenAllDocHistory?.()} className="rounded hover-shadow1">
                          <div className="flex p-1">
                            <Tooltip message="View All Document History" placement="right" position="bottom">
                              <ClockIcon className="w-5 h-5" />
                            </Tooltip>
                          </div>
                        </button>
                      )}
                      <button
                        onClick={previousStatuses ? onUndoResetStatuses : onResetAllStatuses}
                        className="rounded hover-shadow1"
                      >
                        <div className="flex p-1">
                          <Tooltip
                            message={previousStatuses ? 'Undo Reset Status' : 'Reset Document Status'}
                            placement="right"
                            position="bottom"
                          >
                            {previousStatuses ? (
                              <ArrowUturnLeftIcon className="w-5 h-5 text-orange-500" />
                            ) : (
                              <ArrowPathIcon className="w-5 h-5" />
                            )}
                          </Tooltip>
                        </div>
                      </button>
                    </div>
                  </div>

                  <input ref={fileInputRef} type="file" className="hidden" multiple onChange={onFileInputChange} />
                  <Tooltip message="Upload Files" placement="right" position="bottom">
                    <button
                      type="button"
                      className="hover:underline p-1 hover-shadow1 rounded"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <ArrowUpTrayIcon className="w-5 h-5" />
                    </button>
                  </Tooltip>

                  <button className="hover:underline p-1 rounded hover-shadow1" onClick={onShowDocuments}>
                    {floater.side === 'left' ? (
                      <ArrowLeftCircleIcon className="w-5 h-5" />
                    ) : (
                      <ArrowRightCircleIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <NewDocumentsTable
                files={uploadingFiles}
                setFiles={setUploadingFiles}
                onSubmit={async (files: DocumentFile[]) => {
                  await onAdd(files)
                  setUploadingFiles([])
                }}
                onCancel={() => setUploadingFiles([])}
              />
              <div className="p-1">
                {!uploadingFiles.length && (
                  <TargetBox
                    canDrop={(data: any) => {
                      return (
                        !!data?.files ||
                        isForeignDocumentDrop(
                          data,
                          documents.map((doc: LoanSubmissionDocument) => doc.key),
                        )
                      )
                    }}
                    types={['document', NativeTypes.FILE]}
                    onDrop={onDropFiles}
                  >
                    <>
                      <div className="w-full h-fit">
                        <div className="w-full p-2">
                          <Input2
                            type="search"
                            title="Search"
                            hasIcon
                            transparent
                            icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
                            value={searchQuery}
                            onChange={(value) => setSearchQuery(value)}
                          />
                        </div>
                      </div>
                      <div className="relative overflow-y-auto w-full sm:w-[calc(40rem-8px)] h-[calc(90vh-150px)]">
                        <table className="w-full font-normal text-sm text-left text-black relative">
                          <thead className="border-b bg-black/5">
                            <tr>
                              <th
                                className="px-3 border-r border-slate-300 cursor-pointer"
                                onClick={() => onSort('conditionNo')}
                              >
                                {renderHeader('conditionNo', <>Condition #</>)}
                              </th>
                              <th
                                colSpan={4}
                                className="px-2 py-1 border-b border-slate-300 cursor-pointer"
                                onClick={() => onSort('name')}
                              >
                                {renderHeader('name', 'Name')}
                              </th>
                            </tr>
                            <tr>
                              <th className="px-3 border-r border-slate-300 border-t font-variation-settings-500 w-10">
                                Internal / Borrower
                              </th>
                              <th
                                className="px-2 py-1 border-r border-slate-300 cursor-pointer"
                                onClick={() => onSort('status')}
                              >
                                {renderHeader('status', 'Status')}
                              </th>
                              <th
                                className="px-2 border-r border-slate-300 cursor-pointer"
                                onClick={() => onSort('category')}
                              >
                                {renderHeader('category', 'Category')}
                              </th>
                              <th
                                className="px-2 border-r border-slate-300 cursor-pointer"
                                onClick={() => onSort('createdAt')}
                              >
                                {renderHeader('createdAt', 'Uploaded Date')}
                              </th>
                              <th className="px-2 font-normal">Action</th>
                            </tr>
                          </thead>

                          <tbody className="text-gray-900">
                            {sortedDocuments.map((document, index: number) => renderRow(document, index))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  </TargetBox>
                )}
              </div>
            </>
          </HorzScrollContainer>
        </div>
      )}
      {isEmailDialog && <LoanSendEmailDialog documents={selectedDocuments} onClose={onEmailDialogClose} />}
      {!!mailingDocument && (
        <MailDocumentModal
          loanNumber={Number(Api.getLoanNumber())}
          documentKey={mailingDocument.key}
          documentName={mailingDocument.name || mailingDocument.key?.split('/').pop() || 'document.pdf'}
          onClose={() => setMailingDocument(null)}
        />
      )}
      {modalAction == 'analyzeDocument' && activeDocument && activeDocumentData.sections && (
        <ExtractDocumentSectionsModal
          document={activeDocument}
          sections={activeDocumentData.sections}
          onClose={() => {
            setModalOpen('')
            setActiveDocumentData(null)
          }}
          onSubmit={onExtractAndSaveSections}
        />
      )}

      {modalAction == 'analyzeDocument' && activeDocument && activeDocumentData.files && (
        <ExtractDocumentFilesModal
          document={activeDocument}
          files={activeDocumentData.files}
          onClose={() => {
            setModalOpen('')
            setActiveDocumentData(null)
          }}
        />
      )}
      {modalAction == 'analyzeDocumentLogs' && <ExtractDocumentHistoryModal onClose={() => setModalOpen('')} />}
    </>
  )
}
